import { isEmpty } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Inputs, ModelInference } from 'interfaces/calculateFeatures';
import GridBasedCalculationParams from 'interfaces/girdBasedCalculationParams';
import { CalculateFeaturesJob, CalculateFeaturesParams } from 'interfaces/job';
import { PostProcessingActionCreated } from 'interfaces/postProcessingAction';
import { VisualizationCreated } from 'interfaces/visualization';
import { ModelPreferencesById } from 'utils/models';
import { useJob } from 'utils/queryHooks/jobs/useJob';
import { RegistrationDetails } from 'utils/slideRegistrations';

interface UsePreviousJobRestoreParams {
  jobId?: string;
  canApplyJobParams: boolean;
  canRestoreInferenceResults: boolean;
  setCalculateFeatureParams: (params: {
    postprocessing: PostProcessingActionCreated[];
    features: PostProcessingActionCreated[];
    visualizations: VisualizationCreated[];
    gridBasedCalculationParams: { [key: string]: GridBasedCalculationParams };
    modelPreferencesByModelId?: ModelPreferencesById;
    registrations: RegistrationDetails[];
    newCellsCreated?: Record<string, unknown>;
  }) => void;
  applyPipsStudyInputs: (pipsStudyInputs?: CalculateFeaturesParams['pipsStudyInputs'] | null) => void;
  applySlideRegistrations: (registrations?: RegistrationDetails[] | null) => void;
  setModelResults: (params: { inputs?: Inputs }, allInferenceResults: ModelInference[]) => void;
  setJobSummaryData: (params: { name: string; description: string }) => void;
  inferenceArtifacts: ModelInference[];
  onSelectedJobReset?: () => void;
}

export const usePreviousJobRestore = ({
  jobId,
  canApplyJobParams,
  canRestoreInferenceResults,
  setCalculateFeatureParams,
  applyPipsStudyInputs,
  applySlideRegistrations,
  setModelResults,
  setJobSummaryData,
  inferenceArtifacts,
  onSelectedJobReset,
}: UsePreviousJobRestoreParams) => {
  const [selectedJobId, setSelectedJobIdState] = useState(jobId ?? '');
  const [isLoadingSelectedPreviousJob, setIsLoadingSelectedPreviousJob] = useState(false);
  const [jobIdWithRebuildParametersSet, setJobIdWithRebuildParametersSet] = useState('');
  const [isRebuildInferenceResultsSet, setIsRebuildInferenceResultsSet] = useState(false);
  const latestCallbacksRef = useRef({
    setCalculateFeatureParams,
    applyPipsStudyInputs,
    applySlideRegistrations,
    setModelResults,
    setJobSummaryData,
    onSelectedJobReset,
  });

  useEffect(() => {
    latestCallbacksRef.current = {
      setCalculateFeatureParams,
      applyPipsStudyInputs,
      applySlideRegistrations,
      setModelResults,
      setJobSummaryData,
      onSelectedJobReset,
    };
  }, [
    setCalculateFeatureParams,
    applyPipsStudyInputs,
    applySlideRegistrations,
    setModelResults,
    setJobSummaryData,
    onSelectedJobReset,
  ]);

  const handleSelectedJob = useCallback((selectedJob?: { id: string }) => {
    setSelectedJobIdState(selectedJob?.id ?? '');
    setIsRebuildInferenceResultsSet(false);
    setJobIdWithRebuildParametersSet('');
    latestCallbacksRef.current.onSelectedJobReset?.();
  }, []);

  useEffect(() => {
    if (jobId) {
      handleSelectedJob({ id: jobId });
    }
  }, [jobId, handleSelectedJob]);

  const {
    data: job,
    isFetched: isJobFetched,
    isLoading: isJobLoading,
    isError: isJobError,
    error: jobError,
  } = useJob<CalculateFeaturesJob>(selectedJobId, {
    enabled: Boolean(selectedJobId),
    retry: false,
  });

  useEffect(() => {
    const isRebuildParametersSet = selectedJobId === jobIdWithRebuildParametersSet;

    if (!isEmpty(selectedJobId) && !isRebuildParametersSet && isJobFetched && canApplyJobParams) {
      const jobParams = job?.params as CalculateFeaturesParams;

      latestCallbacksRef.current.setCalculateFeatureParams(jobParams);
      latestCallbacksRef.current.applyPipsStudyInputs(jobParams?.pipsStudyInputs);
      latestCallbacksRef.current.applySlideRegistrations(jobParams?.registrations);
      setJobIdWithRebuildParametersSet(selectedJobId);
    }
  }, [selectedJobId, jobIdWithRebuildParametersSet, isJobFetched, canApplyJobParams, job]);

  useEffect(() => {
    if (canRestoreInferenceResults && !isRebuildInferenceResultsSet && !isEmpty(job)) {
      latestCallbacksRef.current.setJobSummaryData({ name: job?.name, description: job?.description });

      if (!isEmpty(job?.params?.inputs)) {
        latestCallbacksRef.current.setModelResults(job.params as CalculateFeaturesParams, inferenceArtifacts);
      }

      setIsRebuildInferenceResultsSet(true);
    }
  }, [canRestoreInferenceResults, isRebuildInferenceResultsSet, job, inferenceArtifacts]);

  const hasLoadedPreviousJobSelections =
    !isEmpty(selectedJobId) &&
    selectedJobId === jobIdWithRebuildParametersSet &&
    isRebuildInferenceResultsSet &&
    Boolean(job);

  return {
    selectedJobId,
    handleSelectedJob,
    isLoadingSelectedPreviousJob,
    setIsLoadingSelectedPreviousJob,
    job,
    isJobFetched,
    isJobLoading,
    isJobError,
    jobError,
    isRebuildInferenceResultsSet,
    hasLoadedPreviousJobSelections,
  };
};
